/**
 * Helpers for keeping ``?channel=`` glued to the URL during navigation.
 *
 * Without these, every ``<Link>`` and ``navigate()`` that hardcodes a
 * ``search`` object drops the channel param, the sidebar falls back to
 * the user's default channel, and the visible context flips out from
 * under the user. The convention is: every route that the sidebar
 * channel switcher targets should accept ``channel`` in its
 * ``validateSearch``, and every navigation should call one of these
 * helpers so it forwards what's already in the URL.
 */

/**
 * ``search`` callback for ``<Link>`` / ``navigate()`` that yields the
 * minimum needed to keep the channel context. Pass when the destination
 * route accepts only ``channel`` (jobs, settings, ...).
 *
 * @example
 *   <Link to="/jobs" search={forwardChannel} />
 */
export function forwardChannel(prev: Record<string, unknown>): { channel?: string } {
  const ch = prev['channel']
  return typeof ch === 'string' && ch ? { channel: ch } : {}
}

/**
 * ``search`` callback that merges new params on top of the current
 * channel. Use when the destination has its own search shape but
 * ``channel`` should still ride along.
 *
 * @example
 *   <Link
 *     to="/projects/$projectId"
 *     params={{ projectId: id }}
 *     search={preservingChannel({ lang })}
 *   />
 */
export function preservingChannel<T extends Record<string, unknown>>(
  extra: T,
): (prev: Record<string, unknown>) => T & { channel?: string } {
  return (prev) => {
    const ch = prev['channel']
    if (typeof ch === 'string' && ch) {
      return { ...extra, channel: ch }
    }
    return extra
  }
}
